import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import React, { useEffect, useMemo, useState } from 'react';
import { FiCheckCircle, FiClock, FiDownload, FiFileText, FiHeart, FiMusic, FiSearch, FiShoppingBag } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { app } from '../firebase';
import './Library.css';

const db = getFirestore(app);
const auth = getAuth(app);

const parseStoredTracks = (key) => {
    try {
        const stored = localStorage.getItem(key);
        const parsed = stored ? JSON.parse(stored) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const formatDate = (value) => {
    if (!value) return 'Recent';
    const date = value?.toDate ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? 'Recent' : date.toLocaleDateString();
};

const formatPrice = (price) => {
    const numericPrice = Number(price || 0);
    return Number.isFinite(numericPrice) ? `$${numericPrice.toFixed(2)}` : '$0.00';
};

const Library = () => {
    const [user, setUser] = useState(() => auth.currentUser);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [purchases, setPurchases] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const [activeTab, setActiveTab] = useState('purchased');

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, setUser);
        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, []);

    useEffect(() => {
        setWishlist(parseStoredTracks('wishlist'));
        setRecentlyViewed(parseStoredTracks('recentlyViewed'));
    }, []);

    useEffect(() => {
        const fetchPurchases = async () => {
            if (!user) {
                setPurchases([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            setError('');

            try {
                const purchaseQuery = query(collection(db, 'purchases'), where('buyerId', '==', user.uid));
                const purchaseSnapshot = await getDocs(purchaseQuery);
                const purchaseRows = purchaseSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                purchaseRows.sort((a, b) => {
                    const aTime = a.purchaseDate?.toMillis?.() || new Date(a.purchaseDate || 0).getTime();
                    const bTime = b.purchaseDate?.toMillis?.() || new Date(b.purchaseDate || 0).getTime();
                    return bTime - aTime;
                });

                setPurchases(purchaseRows);
            } catch (err) {
                console.error('Error loading library:', err);
                setError('Failed to load your library.');
            } finally {
                setLoading(false);
            }
        };

        fetchPurchases();
    }, [user]);

    const libraryStats = useMemo(() => {
        const totalSpent = purchases.reduce((sum, purchase) => sum + Number(purchase.price || 0), 0);
        return [
            { label: 'Purchased beats', value: purchases.length },
            { label: 'Saved beats', value: wishlist.length },
            { label: 'Recently viewed', value: recentlyViewed.length },
            { label: 'Total licensed', value: formatPrice(totalSpent) },
        ];
    }, [purchases, wishlist, recentlyViewed]);

    const tabs = [
        { id: 'purchased', label: 'Purchased', count: purchases.length, icon: FiShoppingBag },
        { id: 'saved', label: 'Saved', count: wishlist.length, icon: FiHeart },
        { id: 'recent', label: 'Recent', count: recentlyViewed.length, icon: FiClock },
        { id: 'licenses', label: 'Licenses', count: purchases.length, icon: FiFileText },
    ];

    const renderArtwork = (track, title) => (
        <div className="library-track-artwork">
            {track?.coverImage ? (
                <img src={track.coverImage} alt={title || 'Beat artwork'} />
            ) : (
                <FiMusic />
            )}
        </div>
    );

    const renderPurchased = () => {
        if (!user) {
            return (
                <div className="library-empty-state">
                    <FiShoppingBag />
                    <h2>Log in to see your library</h2>
                    <p>Your purchased beats, saved tracks, downloads, and licenses will live here.</p>
                    <Link to="/marketplace" className="library-primary-action">Browse Beats</Link>
                </div>
            );
        }

        if (purchases.length === 0) {
            return (
                <div className="library-empty-state">
                    <FiShoppingBag />
                    <h2>No purchases yet</h2>
                    <p>When you license beats, this page becomes your download and contract hub.</p>
                    <Link to="/marketplace" className="library-primary-action">Find Your First Beat</Link>
                </div>
            );
        }

        return (
            <div className="library-list">
                {purchases.map((purchase) => (
                    <article key={purchase.id} className="library-row">
                        {renderArtwork(purchase, purchase.trackTitle)}
                        <div className="library-row-main">
                            <span className="library-row-kicker">Licensed {formatDate(purchase.purchaseDate)}</span>
                            <h3>{purchase.trackTitle || 'Untitled beat'}</h3>
                            <p>{purchase.trackArtist || 'Unknown producer'}</p>
                            <div className="library-row-tags">
                                <span>Standard license</span>
                                <span>{purchase.status || 'completed'}</span>
                                <span>$0 buyer fees</span>
                            </div>
                        </div>
                        <div className="library-row-actions">
                            <strong>{formatPrice(purchase.price)}</strong>
                            <button type="button" className="library-icon-button" aria-label="Download beat">
                                <FiDownload />
                            </button>
                            <button type="button" className="library-secondary-action">View License</button>
                        </div>
                    </article>
                ))}
            </div>
        );
    };

    const renderSaved = () => {
        if (wishlist.length === 0) {
            return (
                <div className="library-empty-state">
                    <FiHeart />
                    <h2>No saved beats yet</h2>
                    <p>Save beats from Marketplace when you want to compare options before buying.</p>
                    <Link to="/marketplace" className="library-primary-action">Browse Marketplace</Link>
                </div>
            );
        }

        return (
            <div className="library-card-grid">
                {wishlist.map((track) => (
                    <article key={track.id} className="library-track-card">
                        {renderArtwork(track, track.title)}
                        <div>
                            <h3>{track.title || 'Untitled beat'}</h3>
                            <p>{track.artist || 'Unknown producer'}</p>
                        </div>
                        <div className="library-card-footer">
                            <span>{formatPrice(track.price)}</span>
                            <Link to="/marketplace" className="library-card-link">License</Link>
                        </div>
                    </article>
                ))}
            </div>
        );
    };

    const renderRecent = () => {
        if (recentlyViewed.length === 0) {
            return (
                <div className="library-empty-state">
                    <FiClock />
                    <h2>No listening history yet</h2>
                    <p>Preview beats in Marketplace and your recent finds will show here.</p>
                    <Link to="/marketplace" className="library-primary-action">Start Listening</Link>
                </div>
            );
        }

        return (
            <div className="library-card-grid">
                {recentlyViewed.map((track) => (
                    <article key={track.id} className="library-track-card">
                        {renderArtwork(track, track.title)}
                        <div>
                            <h3>{track.title || 'Untitled beat'}</h3>
                            <p>{track.artist || 'Unknown producer'}</p>
                        </div>
                        <div className="library-card-footer">
                            <span>{track.genre || 'Beat'}</span>
                            <Link to="/marketplace" className="library-card-link">Reopen</Link>
                        </div>
                    </article>
                ))}
            </div>
        );
    };

    const renderLicenses = () => {
        if (purchases.length === 0) {
            return (
                <div className="library-empty-state">
                    <FiFileText />
                    <h2>No licenses yet</h2>
                    <p>License details will appear here after purchase so artists can always find terms later.</p>
                    <Link to="/marketplace" className="library-primary-action">Explore Beats</Link>
                </div>
            );
        }

        return (
            <div className="license-list">
                {purchases.map((purchase) => (
                    <article key={purchase.id} className="license-card">
                        <div>
                            <span className="library-row-kicker">License record</span>
                            <h3>{purchase.trackTitle || 'Untitled beat'}</h3>
                            <p>{purchase.trackArtist || 'Unknown producer'} · Purchased {formatDate(purchase.purchaseDate)}</p>
                        </div>
                        <div className="license-details">
                            <span><FiCheckCircle /> MP3/WAV access</span>
                            <span><FiCheckCircle /> Clear usage terms</span>
                            <span><FiCheckCircle /> No added buyer fee</span>
                        </div>
                    </article>
                ))}
            </div>
        );
    };

    const renderActiveTab = () => {
        if (loading) {
            return (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Loading your library...</p>
                </div>
            );
        }

        if (activeTab === 'saved') return renderSaved();
        if (activeTab === 'recent') return renderRecent();
        if (activeTab === 'licenses') return renderLicenses();
        return renderPurchased();
    };

    return (
        <div className="library-page">
            <div className="library-shell">
                <header className="library-hero">
                    <div>
                        <span className="library-kicker">Artist library</span>
                        <h1>Your beats, licenses, and downloads.</h1>
                        <p>Keep every purchased beat, saved idea, and license record in one clean workspace.</p>
                        <div className="library-hero-actions">
                            <Link to="/marketplace" className="library-primary-action">
                                <FiSearch />
                                Find Beats
                            </Link>
                            <Link to="/upload" className="library-secondary-action">Sell Beats</Link>
                        </div>
                    </div>
                    <aside className="library-promise-card">
                        <span>Checkout promise</span>
                        <strong>$0 buyer fees</strong>
                        <p>The beat price stays clear. Your library keeps proof, downloads, and license terms together.</p>
                    </aside>
                </header>

                <section className="library-stats-grid" aria-label="Library stats">
                    {libraryStats.map((stat) => (
                        <div key={stat.label} className="library-stat-card">
                            <strong>{stat.value}</strong>
                            <span>{stat.label}</span>
                        </div>
                    ))}
                </section>

                {error && (
                    <div className="message error-message">
                        <span>{error}</span>
                    </div>
                )}

                <section className="library-workspace">
                    <nav className="library-tabs" aria-label="Library sections">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={`library-tab ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <Icon />
                                    <span>{tab.label}</span>
                                    <strong>{tab.count}</strong>
                                </button>
                            );
                        })}
                    </nav>
                    <div className="library-panel">
                        {renderActiveTab()}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Library;
